import { OmneoList, OmneoListDefinitionType, OmneoListItem, typeInStoreItemSupplementalParsed, typeInStoreItemSupplementalRaw } from '../types'
import { OmneoListItemInStore } from '../types/omneo-wishlist-item'
import { type_GM_STATE } from '../../helpers'
import { getManyProductsByManySkus } from '../../helpers/storefront-graphql-queries'
import { executeGraphQlQuery, getIdFromGqlAsNumber } from '../../helpers/storefront-graphql-helper'

declare var GM_STATE: type_GM_STATE

export const tokenNames = {
  omneo: {
    cookie: 'omneoToken',
    localStorageIdBase: 'shapes:omneo:shapestoken:'
  }
}

// known url for dummy image on Cin7 - don't have a better method than this
const knownCin7DefaultImageUrls = [
  'https://go.cin7.com/webfiles/SarahandSebastiaAU/webpages/images/621637/59111_PriceBreak_59150.jpg',
  'https://go.cin7.com/webfiles/SarahandSebastiaAU/webpages/images/621637/55710_PriceBreak_55749.jpg'
]

export const hasOwnProperties = (property: string, obj: object) => {
  return Object.prototype.hasOwnProperty.call(obj, property)
}

/**
 * Child items such as charms, letters, engravings should not be shown in the wishlist; nor items missing skus
 */
export const itemShouldBeHidden = (item: OmneoListItem | OmneoListItemInStore): boolean => {
  // to find the patterns of sku and title to use, run:
  /*
    let lists = await omneoService.sdk.get('lists').then(res => res.body.data)
    let allItems = lists.flatMap(list => list.items)
    let targetProps = allItems.map(item => { 
        const { title, sku } = item.product_variant
        return { title, sku }
    })
    console.table(targetProps)
  */

  const hideChildItemFragments = GM_STATE.wishlist.hideWishlistItemsBySkuFragment
  const { title, sku } = item.product_variant

  const titleContainsFragment = hideChildItemFragments.some((fragment) => {
    if (fragment === '') return false
    return title.includes(fragment)
  })
  const skuContainsFragment = hideChildItemFragments.some((fragment) => {
    if (fragment === '') return false
    return sku.includes(fragment)
  })
  const itemIsMissingSku = !item.product_variant.sku

  if (titleContainsFragment || skuContainsFragment || itemIsMissingSku) {
    return true
  }

  return false
}

/**
 * TODO - give this function a better name. It's scanning for item meta, not list validity
 */
export function listMadeOnSiteIsValid(list: OmneoList): boolean {
  // filter out any storemade list, with items, which has malformed meta props
  if (list.definition.type !== 'default') return true

  // allow empty lists
  if (!list.items.length) return true

  // only if all data is malformed - has bigger effect in detail page, causes a blank screen
  if (list.items.every((item) => item.meta === null)) return false

  return true
}

export function getWishlistListCoverImage(list: OmneoList): { src: string; alt: string } {
  const { imageDefault } = GM_STATE.wishlist
  let imageSrc =
    list.meta?.image?.src || list.items.find((x) => x?.meta?.image?.src)?.meta.image.src || list.items.find((x) => x?.meta?.image)?.meta.image || ''

  if (!imageSrc) {
    const itemWithImage = list.items.find((item) => {
      if (itemShouldBeHidden(item)) return false

      const firstImageUrl = item.product?.images?.[0]?.url
      // known url for dummy image on Cin7 - don't have a better method than this
      if (knownCin7DefaultImageUrls.includes(firstImageUrl)) return false

      return item.product?.images?.[0]?.url
    })
    if (itemWithImage) {
      imageSrc = itemWithImage.product.images[0].url
    }
  }

  const image = {
    src: imageSrc?.toString() || imageDefault.src,
    alt: list.name || imageDefault.alt || ''
  }

  return image
}

export async function getStorefrontProductsBySkus(skus: string[]): Promise<Array<typeInStoreItemSupplementalParsed>> {
  const gqlQuery = getManyProductsByManySkus(skus)
  return await executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) {
        console.error('res not ok', res)
        throw res
      }
      return res.json()
    })
    .then((data) => {
      if (data.errors) {
        throw data
      }

      const products = data.data.search.nodes
        .map((product: typeInStoreItemSupplementalRaw) => {
          if (!product.handle || !product.variants) {
            console.error('Could not find handle or variants on product node in OmneoService.getLists', product)
            return null
          }
          const variants = product.variants.nodes.map((variant) => {
            return {
              id: variant.id,
              availableForSale: variant.availableForSale,
              sku: variant.sku,
              price: variant.price.amount,
              selectedOptions: variant.selectedOptions
            }
          })
          const skus = variants.map((x) => x.sku)

          return {
            id: product.id,
            title: product.title,
            handle: product.handle,
            productBiline: product.productBiline?.value || '',
            variants,
            skus
          }
        })
        // filter out nullish
        .filter((x: any) => x)

      return products
    })
    .catch((err) => {
      console.error('Could not fetch products from storefront during getLists', err)
      return []
    })
}

export function attachRequiredStorefrontInformationToOmneoItem(
  item: OmneoListItem | OmneoListItemInStore,
  storefrontProducts: typeInStoreItemSupplementalParsed[],
  listDefinition: OmneoListDefinitionType
): OmneoListItem | OmneoListItemInStore {
  const sku = item.product_variant.sku
  const storefrontProduct = storefrontProducts.find((product) => product.skus.includes(sku))
  const storefrontVariantPrice = storefrontProduct?.variants.find((variant) => variant.sku === sku)?.price ?? ''
  const storefrontVariant = storefrontProduct?.variants.find((variant) => variant.sku === sku)
  const variantId = getIdFromGqlAsNumber(storefrontVariant?.id || '')
  const productId = getIdFromGqlAsNumber(storefrontProduct?.id || '')

  item.meta = item.meta || {}

  // CDSD-29896
  // always need to forcefully set the price - the Omneo list currently stores the price, but doesn't know if its correct or not
  if (storefrontVariantPrice) {
    item.meta.variantPrice = storefrontVariantPrice
  }

  // don't need to redefine any data on any list EXCEPT those made in Omneo
  if (listDefinition !== 'store') return item

  const handle = storefrontProduct?.handle || ''
  const title = storefrontProduct?.title || item.product.title

  const productBiline = storefrontProduct?.productBiline || ''
  // TODO: get variant price with particular sku

  item.product.handle = handle
  item.product.title = title

  item.meta.productBiline = productBiline
  item.meta.handle = handle
  // Missing meta data that if instore products are added to the list
  item.meta.templateSuffix = 'default'
  item.meta.variantOptions = storefrontVariant?.selectedOptions.map((option) => option.value) || []
  item.meta.variantAvailable = storefrontVariant?.availableForSale || false
  item.meta.variantSku = sku
  item.meta.variantId = variantId
  item.meta.productId = productId

  // handle cases where the item is now in draft mode / has been discontinued
  if (!storefrontProduct) {
    item.meta.discontinued = true
  }

  return item
}
